import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingIndicator } from '../../components/Spinner';
import type { MacroAction } from '../shortcuts/api';
import { ActionEditor } from './ActionEditor';
import { ConditionEditor } from './ConditionEditor';
import { type ConditionGroup } from './conditions';
import { useCreateJob, useDeleteJob, useJobs, useRunJobs, useUpdateJob } from './queries';

/**
 * Zeitgesteuerte Massenaktionen -- F3-13, K-59.
 *
 * `last_run_at`, `last_run_count` und `last_error` stehen in der Liste, weil
 * ein Zeitplan, der nichts mehr tut, sonst niemandem auffaellt -- es geschieht
 * einfach nichts mehr. Dieselbe Ueberlegung wie bei den Postfaechern.
 *
 * "Jetzt laufen lassen" ist der Handgriff zum Ausprobieren. Im Betrieb ruft
 * der Taktgeber (`itscrm.workers.scheduler`) denselben Weg jede Minute; ein
 * Auftrag laeuft je Minute trotzdem hoechstens einmal.
 */
const EMPTY_CONDITION: ConditionGroup = { operator: 'and', rules: [] };

export function JobsPanel() {
  const { t } = useTranslation();
  const jobs = useJobs();
  const create = useCreateJob();
  const update = useUpdateJob();
  const remove = useDeleteJob();
  const run = useRunJobs();

  const [name, setName] = useState('');
  const [cron, setCron] = useState('0 2 * * *');
  const [selector, setSelector] = useState<ConditionGroup>(EMPTY_CONDITION);
  const [actions, setActions] = useState<MacroAction[]>([]);

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('automation.newJob')}</h2>

        <div className="field">
          <label className="field__label" htmlFor="job-name">
            {t('masterdata.name')}
          </label>
          <input
            id="job-name"
            className="field__control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="job-cron">
            {t('automation.cron')}
          </label>
          <input
            id="job-cron"
            className="field__control"
            value={cron}
            onChange={(event) => {
              setCron(event.target.value);
            }}
          />
          <span className="field__hint">{t('automation.cronHint')}</span>
        </div>

        <h3>{t('automation.selector')}</h3>
        <ConditionEditor
          value={selector}
          onChange={setSelector}
          emptyHint={t('automation.emptySelectorHint')}
        />

        <h3>{t('macros.actions')}</h3>
        <ActionEditor value={actions} onChange={setActions} />

        {create.error ? <ErrorNotice error={create.error} /> : null}

        <Button
          variant="primary"
          disabled={name.trim() === '' || actions.length === 0 || create.isPending}
          onClick={() => {
            create.mutate(
              {
                name: name.trim(),
                description: '',
                cron_expression: cron.trim(),
                selector: selector.rules.length > 0 ? selector : {},
                actions,
                is_active: true,
              },
              {
                onSuccess: () => {
                  setName('');
                  setSelector(EMPTY_CONDITION);
                  setActions([]);
                },
              },
            );
          }}
        >
          {t('masterdata.add')}
        </Button>
      </section>

      <section className="card">
        <div className="page-header">
          <h2>{t('automation.jobs')}</h2>
          <Button
            variant="secondary"
            disabled={run.isPending}
            onClick={() => {
              run.mutate();
            }}
          >
            {t('automation.runNow')}
          </Button>
        </div>

        {run.data ? (
          <p className="table__muted">
            {run.data.length === 0
              ? t('automation.nothingDue')
              : t('automation.jobsRan', {
                  jobs: run.data.length,
                  tickets: run.data.reduce((sum, entry) => sum + entry.matched, 0),
                })}
          </p>
        ) : null}
        {run.error ? <ErrorNotice error={run.error} /> : null}

        {jobs.isPending ? <LoadingIndicator /> : null}
        {jobs.error ? <ErrorNotice error={jobs.error} /> : null}
        {jobs.data?.length === 0 ? <p className="table__muted">{t('automation.noJobs')}</p> : null}

        <ul className="task-list">
          {(jobs.data ?? []).map((job) => (
            <li key={job.id} className="task-list__item task-list__item--column">
              <div className="row">
                <strong>{job.name}</strong>
                <Badge tone={job.is_active ? 'success' : 'neutral'}>
                  {job.is_active ? t('automation.active') : t('automation.inactive')}
                </Badge>
                <code>{job.cron_expression}</code>
              </div>

              <p className="table__muted">
                {job.last_run_at
                  ? t('automation.lastRun', {
                      when: new Date(job.last_run_at).toLocaleString(),
                      count: job.last_run_count,
                    })
                  : t('automation.neverRan')}
              </p>

              {/* Der Text stammt vom Server und beschreibt den Lauf -- er ist
                  Betriebsdatum wie der Name des Auftrags und keine
                  Oberflaechenzeichenkette. */}
              {job.last_error ? (
                <p className="notice notice--info" role="status">
                  {job.last_error}
                </p>
              ) : null}

              <div className="row">
                <Button
                  variant="ghost"
                  onClick={() => {
                    update.mutate({ jobId: job.id, payload: { is_active: !job.is_active } });
                  }}
                >
                  {job.is_active ? t('automation.deactivate') : t('automation.activate')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(t('masterdata.deleteConfirm'))) {
                      remove.mutate(job.id);
                    }
                  }}
                >
                  {t('masterdata.delete')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
